import { logError } from '../lib/logger';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { InlineSelect } from './StatusBadge';
import { getGoogleReviews, createGoogleReview, updateGoogleReview, deleteGoogleReview } from '../lib/api';
import { Plus, Trash, Star, EnvelopeSimple, Phone, CheckCircle, ThumbsUp, Trophy } from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOAN_OFFICERS = ['Eric Hamilton', 'Tom Eboli', 'Fabian Herrera', 'Mike Pelagio'];
const LOAN_TYPES = ['Purchase', 'Refinance', 'HELOC', 'VA', 'FHA', 'Conventional', 'IHDA'];
const EXPERIENCE_RATINGS = ['Excellent', 'Good', 'Average', 'Below Average'];
const OUTREACH_STATUSES = ['Not Started', 'Called', 'Called + Email Sent', 'Confirmed Will Review', 'Declined', 'Completed'];

const EXPERIENCE_COLORS = {
  'Excellent': 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  'Good': 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
  'Average': 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  'Below Average': 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
};

const OUTREACH_COLORS = {
  'Not Started': 'bg-stone-500/15 text-stone-600 dark:text-stone-400 border-stone-500/30',
  'Called': 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30',
  'Called + Email Sent': 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30',
  'Confirmed Will Review': 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  'Declined': 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
  'Completed': 'bg-emerald-600 text-white border-emerald-600',
};

// ─── Sub-components ───

function ReviewRow({ item, loOptions, onInlineUpdate, onDelete }) {
  return (
    <TableRow className={`border-border hover:bg-muted/30 transition-colors ${item.review_submitted ? 'opacity-60' : ''}`} data-testid={`review-row-${item.id}`}>
      <TableCell className="pl-4 font-medium text-foreground text-sm">
        <div className="flex items-center gap-1.5">
          {item.experience_rating === 'Excellent' && !item.review_submitted && <Star size={13} weight="fill" className="text-amber-500" />}
          {item.borrower}
        </div>
      </TableCell>
      <TableCell>
        <InlineSelect value={item.loan_officer || 'None'} options={loOptions}
          onChange={(v) => onInlineUpdate(item.id, 'loan_officer', v === 'None' ? '' : v)} testId={`inline-rv-lo-${item.id}`} />
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">{item.loan_type || '-'}</TableCell>
      <TableCell>
        <InlineSelect value={item.experience_rating || 'Good'} options={EXPERIENCE_RATINGS}
          onChange={(v) => onInlineUpdate(item.id, 'experience_rating', v)} testId={`inline-rv-exp-${item.id}`} colorMap={EXPERIENCE_COLORS} />
      </TableCell>
      <TableCell>
        <InlineSelect value={item.outreach_status || 'Not Started'} options={OUTREACH_STATUSES}
          onChange={(v) => onInlineUpdate(item.id, 'outreach_status', v)} testId={`inline-rv-outreach-${item.id}`} colorMap={OUTREACH_COLORS} />
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">{item.outreach_date || '-'}</TableCell>
      <TableCell className="text-center">
        <button onClick={() => onInlineUpdate(item.id, 'review_submitted', !item.review_submitted)}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold transition-colors ${
            item.review_submitted ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400 hover:bg-stone-200'
          }`} data-testid={`toggle-review-${item.id}`}>
          <CheckCircle size={13} weight={item.review_submitted ? 'fill' : 'regular'} />
          {item.review_submitted ? 'Yes' : 'No'}
        </button>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">{item.review_date || '-'}</TableCell>
      <TableCell className="text-xs text-muted-foreground max-w-[120px] truncate">{item.notes || '-'}</TableCell>
      <TableCell className="pr-4">
        <Button variant="ghost" size="icon" onClick={() => onDelete(item.id)} className="h-7 w-7 text-red-400 hover:text-red-600" data-testid={`delete-review-${item.id}`}>
          <Trash size={14} />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function ReviewStats({ stats }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      <div className="p-2 rounded-md bg-muted border border-border text-center">
        <span className="text-lg font-bold text-foreground">{stats.total}</span>
        <p className="text-[10px] text-muted-foreground">Total Clients</p>
      </div>
      <div className="p-2 rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-center">
        <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{stats.excellent}</span>
        <p className="text-[10px] text-emerald-600 dark:text-emerald-400">Excellent - Ask First</p>
      </div>
      <div className="p-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-center">
        <span className="text-lg font-bold text-amber-700 dark:text-amber-300">{stats.pending}</span>
        <p className="text-[10px] text-amber-600 dark:text-amber-400">Pending Outreach</p>
      </div>
      <div className="p-2 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-center">
        <span className="text-lg font-bold text-blue-700 dark:text-blue-300">{stats.completed}</span>
        <p className="text-[10px] text-blue-600 dark:text-blue-400">Reviews Received</p>
      </div>
    </div>
  );
}

// ─── Main Component ───

export default function GoogleReviews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ borrower: '', loan_officer: '', loan_type: 'Purchase', experience_rating: 'Good', notes: '' });

  const loadData = useCallback(async () => {
    try { setItems(await getGoogleReviews()); }
    catch (e) { logError('Reviews load error:', e); toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); const iv = setInterval(loadData, 5000); return () => clearInterval(iv); }, [loadData]);

  const loOptions = useMemo(() => ['None', ...LOAN_OFFICERS], []);

  const handleSave = async () => {
    if (!form.borrower.trim()) { toast.error('Borrower required'); return; }
    try { await createGoogleReview(form); toast.success('Added to review list'); setDialogOpen(false); setForm({ borrower: '', loan_officer: '', loan_type: 'Purchase', experience_rating: 'Good', notes: '' }); loadData(); }
    catch (e) { toast.error('Failed'); }
  };

  const handleInlineUpdate = async (id, field, value) => {
    try {
      const update = { [field]: value };
      if (field === 'outreach_status' && value !== 'Not Started' && !items.find(i => i.id === id)?.outreach_date) {
        update.outreach_date = new Date().toISOString().split('T')[0];
      }
      if (field === 'review_submitted' && value === true) {
        update.review_date = new Date().toISOString().split('T')[0];
        update.outreach_status = 'Completed';
      }
      await updateGoogleReview(id, update);
      loadData();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try { await deleteGoogleReview(id); toast.success('Removed'); loadData(); } catch (e) { toast.error('Failed'); }
  };

  // Sort: Excellent experience first (best candidates), then by outreach status
  const prioritized = [...items].sort((a, b) => {
    const exp = { Excellent: 0, Good: 1, Average: 2, 'Below Average': 3 };
    const stat = { 'Not Started': 0, 'Called': 1, 'Called + Email Sent': 2, 'Confirmed Will Review': 3, 'Declined': 5, 'Completed': 4 };
    if (a.review_submitted !== b.review_submitted) return a.review_submitted ? 1 : -1;
    return (exp[a.experience_rating] || 2) - (exp[b.experience_rating] || 2) || (stat[a.outreach_status] || 0) - (stat[b.outreach_status] || 0);
  });

  const stats = {
    total: items.length,
    pending: items.filter(i => !i.review_submitted && i.outreach_status !== 'Declined').length,
    completed: items.filter(i => i.review_submitted).length,
    excellent: items.filter(i => i.experience_rating === 'Excellent' && !i.review_submitted).length,
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-4" data-testid="google-reviews-view">
      {/* Priority Guide */}
      <Card className="border border-border bg-card" data-testid="review-priority-guide">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Trophy size={18} weight="duotone" className="text-amber-500" />
            <h3 className="text-sm font-bold text-foreground">Who to Ask for Reviews</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Prioritize clients with the best experience. Excellent and Good ratings are most likely to leave positive reviews.</p>
          <ReviewStats stats={stats} />
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div />
        <Button onClick={() => setDialogOpen(true)} className="bg-primary text-primary-foreground hover:opacity-90 h-9" data-testid="add-review-btn">
          <Plus size={16} className="mr-1.5" /> Add Client
        </Button>
      </div>

      <Card className="border border-border bg-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pl-4">Client</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Loan Officer</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Type</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Experience</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Outreach</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Outreach Date</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] text-center">Review Done</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Review Date</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em]">Notes</TableHead>
                <TableHead className="text-muted-foreground text-[10px] uppercase tracking-[0.15em] pr-4 w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {prioritized.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No clients added yet</TableCell></TableRow>
              ) : prioritized.map(item => (
                <ReviewRow key={item.id} item={item} loOptions={loOptions}
                  onInlineUpdate={handleInlineUpdate} onDelete={handleDelete} />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">Add Client for Review</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Client Name</label>
                <Input value={form.borrower} onChange={(e) => setForm(p => ({ ...p, borrower: e.target.value }))} className="border-border h-9" data-testid="input-review-borrower" /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Loan Officer</label>
                <Select value={form.loan_officer || 'none'} onValueChange={(v) => setForm(p => ({ ...p, loan_officer: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="border-border h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{LOAN_OFFICERS.map(lo => <SelectItem key={lo} value={lo}>{lo}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Loan Type</label>
                <Select value={form.loan_type} onValueChange={(v) => setForm(p => ({ ...p, loan_type: v }))}>
                  <SelectTrigger className="border-border h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOAN_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Experience Rating</label>
                <Select value={form.experience_rating} onValueChange={(v) => setForm(p => ({ ...p, experience_rating: v }))}>
                  <SelectTrigger className="border-border h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{EXPERIENCE_RATINGS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Notes</label>
              <Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="border-border h-9" placeholder="Any notes about the experience..." data-testid="input-review-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} className="bg-primary text-primary-foreground">Add Client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
